import { prettifyKey } from '~/Utils';

import type { ClaimFlagConfiguration } from './types';

/** Sentinel option matching claims that carry no flags (the backend flag_key[] 'NONE' sentinel). */
export const CLAIM_FLAG_FILTER_NULL_CHOICE = 'NONE';

interface FlagFilterScope {
  subOrganizationId?: number | string | null;
  lob?: string | null;
}

// Mirror the backend scope check (is_flag_in_claim_scope): a flag is offered when it is scoped to all
// sub-orgs / LOBs, or the currently selected sub-org / LOB is in its scope. An unselected axis does not
// narrow — the filter then offers every enabled flag.
const isFlagInFilterScope = (config: ClaimFlagConfiguration, { subOrganizationId, lob }: FlagFilterScope): boolean => {
  const subOrgMatches =
    config.is_all_sub_orgs ||
    !subOrganizationId ||
    (config.sub_organization_ids ?? []).includes(Number(subOrganizationId));
  const lobMatches = config.is_all_lobs || !lob || (config.lobs ?? []).includes(lob);
  return subOrgMatches && lobMatches;
};

/** Enabled flags whose scope matches the (optionally) selected sub-org / LOB, plus the "No flags" option. */
export const getClaimFlagFilterOptions = (
  claimFlagConfigurations: ClaimFlagConfiguration[],
  scope: FlagFilterScope = {}
): string[] =>
  claimFlagConfigurations
    .filter((config) => config.is_enabled && isFlagInFilterScope(config, scope))
    .map((config) => config.key)
    .concat(CLAIM_FLAG_FILTER_NULL_CHOICE);

/**
 * Drop selected flag keys no longer offered for the given scope, so narrowing the sub-org / LOB filter
 * can't leave a now-out-of-scope key selected (and silently sent). When no real flag is in scope the
 * options collapse to just the "No flags" sentinel and the control hides — so clear the selection
 * entirely (sentinel included), otherwise a hidden `['NONE']` would keep filtering with no way to clear it.
 */
export const filterFlagKeysInScope = (
  selectedFlagKeys: string[],
  claimFlagConfigurations: ClaimFlagConfiguration[],
  scope: FlagFilterScope = {}
): string[] => {
  const scopedOptions = getClaimFlagFilterOptions(claimFlagConfigurations, scope);
  // Only the sentinel remains → the control won't render, so nothing should stay selected.
  if (scopedOptions.length <= 1) {
    return [];
  }
  const inScope = new Set(scopedOptions);
  return selectedFlagKeys.filter((key) => inScope.has(key));
};

/**
 * Label for a selected flag filter value. A missing (disabled/deleted) config falls back to the
 * prettified key, matching how the claim-bar chips render the same orphan key.
 */
export const getClaimFlagFilterLabel = (claimFlagConfigurations: ClaimFlagConfiguration[], flagKey: string): string =>
  flagKey === CLAIM_FLAG_FILTER_NULL_CHOICE
    ? 'No flags'
    : claimFlagConfigurations.find((config) => config.key === flagKey)?.label || prettifyKey(flagKey);
